import { useState, useCallback } from 'react';
import { NodeType } from '../types/cytoscape';

// useNodeHighlighting.ts - ハイライトのみ担当
export const useNodeHighlighting = () => {
  // 状態を外部から受け取る
  const [highlightedNode, setHighlightedNode] = useState<string | null>(null);

  const applyHighlighting = (
    cytoscapeCoreRefObject: React.MutableRefObject<cytoscape.Core | null>,
    nodeId: string,
    className: string = 'highlighted'
  ) => {
    const cy = cytoscapeCoreRefObject.current;
    if (!cy || !nodeId) return;

    // Clear previous highlighting classes
    cy.elements().removeClass(className);
    cy.elements().removeClass('faded');

    // Apply highlighting to the node and its connections
    const targetNode = cy.$id(nodeId);
    if (targetNode.length === 0) return;

    targetNode.addClass(className);
    targetNode.neighborhood().addClass(className);

    if (targetNode.isNode() && targetNode.data('nodeType') === NodeType.THEME) {
      targetNode.connectedEdges().addClass('theme-highlighted');
    } else {
      targetNode.connectedEdges().addClass(className);
    }

    // Fade non-highlighted elements
    cy.elements().not(`.${className}`).addClass('faded');
  };

  const removeHighlighting = useCallback(
    (cytoscapeCoreRefObject: React.MutableRefObject<cytoscape.Core | null>) => {
      const cy = cytoscapeCoreRefObject.current;
      if (!cy) return;

      cy.elements().removeClass('highlighted faded');
      setHighlightedNode(null); // ハイライト状態をリセット
    },
    [setHighlightedNode]
  );

  const highlightNodeAndConnections = useCallback(
    (cy: React.MutableRefObject<cytoscape.Core | null>, nodeId: string) => {
      if (!cy.current) return;

      if (highlightedNode === nodeId) {
        // 既にハイライトされている場合は ハイライトを解除
        setHighlightedNode(null);
        removeHighlighting(cy);
      } else {
        // ハイライト処理
        setHighlightedNode(nodeId);
        applyHighlighting(cy, nodeId);
      }
    },
    [highlightedNode, setHighlightedNode, removeHighlighting, applyHighlighting]
  );

  return {
    highlightedNode,
    highlightNodeAndConnections,
    removeHighlighting,
    applyHighlighting,
  };
};
